// src/lib/managers/mail/MailSync.ts

import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { ApiManager } from "../api/ApiManager";
import { toValidDate } from "../customView/customViewMailEntry";
import { SmartLabelStore } from "../customView/SmartLabelStore";
import {
    CustomViewMailEntry,
    CustomViewSortOrder,
    ViewFilter,
} from "../customView/types/customViews";
import { DataManager } from "../dataManager/DataManager";
import { isThrottleError } from "../dataManager/graph/GraphThrottleError";
import { MailFolder } from "../dataManager/graph/types/graph";
import { DataSearchResult } from "../dataManager/IDataApi";
import { LicenseManager } from "../license";
import { FeatureFlag } from "../user";
import { FeatureFlags } from "../user/FeatureFlags";
import { MailAnalysisFlag } from "../user/MailAnalysisFlag";
import { UserProfileLoader } from "../user/UserProfileLoader";
import {
    dedupToCanonical,
    MailboxFolderContext,
    MailCopyRef,
    pickCanonicalCopy,
    uniqueMailvistaId,
} from "./domain/mailIdentity";
import { MailAnalysisState } from "./MailAnalysisState";
import { MailRecord } from "./MailRecord";
import {
    INITIAL_ANALYSIS_BY_DAY_DEFAULT,
    INITIAL_ANALYSIS_MAIL_COUNT,
} from "./types/const";
import {
    AttendeeResponseStatus,
    Mail,
    MailComponentSize,
    MailIds,
    MailViewContent,
} from "./types/mail";
import {
    BaseMail,
    GetMailsFromInternetMessageIdsResponseBody,
    GetMailsInAnyFolderResponseBody,
    isQualifiedMail,
    QualifiedMail,
} from "./types/mail.api";
import { selectLatestInboxMail } from "./utils/selectLatestInboxMail";

/**
 * WS4 — Cible de fetch d'une COPIE précise (slot). `folderId` = dossier visé.
 * `graphId` = handle Graph de la copie (requis pour le chemin vue Dossier = projection directe ;
 * absent pour le chemin canonique/enrichi qui fetch par internetMessageId via l'API).
 */
export type MailCopyFetchTarget = {
    internetMessageId: string;
    folderId: string;
    graphId?: string;
};

/** Chemins de fetch d'une copie. Sert à tracer quel chemin a produit un log de fetch. */
export enum MailCopyFetchPath {
    /** Slot inbox d'un message, via l'API enrichie (analyse + DB). */
    Canonical = "canonical",
    /** Copie d'un dossier précis, par projection Graph directe. */
    Folder = "folder",
    /** Toutes les occurrences d'un message, sans restriction de dossier. */
    AnyFolder = "anyFolder",
}

/** Ce qui diffère entre les chemins de fetch ; `_fetchMissingCopies` fournit tout le reste. */
export type MailCopyFetchStrategy = {
    /** Chemin emprunté — logs uniquement. */
    path: MailCopyFetchPath;
    /** Cible absente du Record ? Critère propre au chemin (un slot précis, ou le message entier). */
    isMissing: (target: MailCopyFetchTarget) => boolean;
    /** Rapatrie les cibles dans le Record. Rejette si une cible échoue → déclenche le retry. */
    fetch: (targets: MailCopyFetchTarget[]) => Promise<void>;
};

export class MailSync {
    private pollingInterval: ReturnType<typeof setTimeout> | null = null;

    private readonly pollingDelayInMs = 20000;

    private mailProcessingInProgress = false;

    private disposed = false;

    public mailRecord: MailRecord;

    private analysisState: MailAnalysisState;

    // Deduplication Map pour les analyses IA en vol (AB#1897).
    // Cle : internetMessageId - Valeur : Promise de l'appel `analysis-save-mail` pour CE mail.
    // Deux passes qui se chevauchent (demarrage + polling, re-init apres coupure reseau) demandaient
    // deux fois l'analyse du meme mail. Le backend ne peut en persister qu'une (contrainte d'unicite
    // `(internet_message_id, user_id)`) : la seconde consommait un appel IA et une unite de quota pour
    // un resultat jete. Jumeau de `_inFlightFetches`. Auto-nettoyee au settle.
    private readonly _inFlightAnalyses = new Map<
        string,
        Promise<QualifiedMail>
    >();

    // Deduplication Map pour fetchMissingMailsIfAnyByInternetMessageIds
    // Clé : internetMessageId — Valeur : Promise du batch API qui fetch cet ID
    private readonly _inFlightFetches = new Map<string, Promise<void>>();

    /**
     * Déduplication des fetch de contenu concurrents : internetMessageId → Promise partagée.
     * Jumeau de _inFlightFetches / _inFlightPreviewFetches. Auto-nettoyée au settle.
     */
    private readonly _inFlightViewContentFetches = new Map<
        string,
        Promise<void>
    >();

    public apiManager: ApiManager;

    public dataManager: DataManager;

    // #region Properties

    public eventBus: EventBus;

    public featureFlags: FeatureFlags;

    public licenseManager: LicenseManager;

    public mailAnalysisFlag: MailAnalysisFlag;

    public profileLoader: UserProfileLoader;

    public readonly smartLabelStore: SmartLabelStore;

    // Analyses IA lancées et pas encore terminées, quel que soit l'appelant
    private readonly _runningAnalysisPasses = new Set<Promise<void>>();

    // Partagée : _processEmailsWithAI est fire-and-forget et joignable depuis une quinzaine de points.
    private _quotaStateRefresh: Promise<void> | null = null;

    constructor(
        eventBus: EventBus,
        mailRecord: MailRecord,
        analysisState: MailAnalysisState,
        smartLabelStore: SmartLabelStore,
        apiManager: ApiManager,
        dataManager: DataManager,
        licenseManager: LicenseManager,
        featureFlags: FeatureFlags,
        profileLoader: UserProfileLoader,
        mailAnalysisFlag: MailAnalysisFlag,
    ) {
        this.eventBus = eventBus;
        this.mailRecord = mailRecord;
        this.analysisState = analysisState;
        this.smartLabelStore = smartLabelStore;
        this.apiManager = apiManager;
        this.dataManager = dataManager;
        this.licenseManager = licenseManager;
        this.featureFlags = featureFlags;
        this.profileLoader = profileLoader;
        this.mailAnalysisFlag = mailAnalysisFlag;
    }

    public _cleanup(): void {
        this.disposed = true;
        this._stopMailPolling();
        this.mailProcessingInProgress = false;
        this._inFlightFetches.clear();
        this._inFlightAnalyses.clear();
        this._inFlightViewContentFetches.clear();
    }

    private get inboxFolderId(): string | undefined {
        return this.mailRecord.inboxFolderId;
    }

    private set inboxFolderId(value: string | undefined) {
        this.mailRecord.inboxFolderId = value;
    }

    private get deleteditemsFolderId(): string | undefined {
        return this.mailRecord.deleteditemsFolderId;
    }

    private set deleteditemsFolderId(value: string | undefined) {
        this.mailRecord.deleteditemsFolderId = value;
    }

    private get _attachmentsFetchedSet(): Set<string> {
        return this.mailRecord._attachmentsFetchedSet;
    }

    private get attachmentPreviewsRecordByKey(): Record<string, string> {
        return this.mailRecord.attachmentPreviewsRecordByKey;
    }

    private set attachmentPreviewsRecordByKey(value: Record<string, string>) {
        this.mailRecord.attachmentPreviewsRecordByKey = value;
    }

    private get mailViewContentRecordByInternetMessageId(): Record<
        string,
        MailViewContent
    > {
        return this.mailRecord.mailViewContentRecordByInternetMessageId;
    }

    private set mailViewContentRecordByInternetMessageId(
        value: Record<string, MailViewContent>,
    ) {
        this.mailRecord.mailViewContentRecordByInternetMessageId = value;
    }

    private get mailsRecordByInternetMessageId(): Record<
        string,
        Map<string, Mail>
    > {
        return this.mailRecord.byInternetMessageId;
    }

    private set mailsRecordByInternetMessageId(
        record: Record<string, Map<string, Mail>>,
    ) {
        this.mailRecord.byInternetMessageId = record;
    }

    /**
     * @method _addMailsToRecordAndLaunchAnalysisForNew
     * -- Transforme les emails en structure de données standard et les ajoute au Record
     * -- Lance l'analyse IA pour les mails n'ayant pas de dbMails correspondants (fire-and-forget)
     * -- TODO - Supprime de DB les mails n'existant plus dans Graph ?
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {(BaseMail | QualifiedMail)[]} mails                    - Emails à ajouter au Record
     * @input {boolean}                      forceMailAnalysis        - Force la ré-analyse même si déjà analysé
     * @input {number}                       batchDelayMs             - Délai entre les batches d'analyse IA
     * @input {Promise<void>[]}              analysisPromisesCollector - [Optionnel] Si fourni, la Promise de
     *                                       _processEmailsWithAI est poussée dans ce tableau afin que l'appelant
     *                                       puisse suivre la fin de l'analyse via Promise.allSettled(collector).
     *                                       Absent = la Promise est fire-and-forget, aucun suivi possible.
     * @output {void} pas de retour
     * @private
     */
    private _addMailsToRecordAndLaunchAnalysisForNew(
        mails: (BaseMail | QualifiedMail)[],
        forceMailAnalysis: boolean = false,
        batchDelayMs: number = 2000,
        analysisPromisesCollector?: Promise<void>[],
    ): void {
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `addMailsToRecord(${mails.length})`,
        );

        const baseMailsToProcess: BaseMail[] = [];

        // Add all mails to Record
        mails.forEach((mail) => {
            if (!mail.internetMessageId) {
                logger.error(
                    [LOGS.MailManager],
                    `Mail without internetMessageId cannot be added to Record`,
                );
                return;
            }

            if (isQualifiedMail(mail)) {
                // TODO - Test to delete if not necessary (comment keep from previous version)
                // Vérifie si le QualifiedMail est supprimé et s'il est focus
                if (
                    mail.parentFolderId === this.deleteditemsFolderId &&
                    mail.isFocus
                ) {
                    logger.warn(
                        [LOGS.TESTS, LOGS.MailManager],
                        `mails with Focus deleted : ${mail.internetMessageId}`,
                    );
                } else {
                    this._setMailInRecord(mail as Mail);
                }
            } else {
                // toujours ajouté au Record et à traiter avec AI
                this._setMailInRecord(mail as Mail);
                baseMailsToProcess.push(mail);
            }
        });

        const qualifiedWithoutScore = mails.filter(
            (mail) => isQualifiedMail(mail) && mail.score == null,
        );
        if (qualifiedWithoutScore.length > 0) {
            logger.warn(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `addMailsToRecord : ${qualifiedWithoutScore.length} mail(s) qualified SANS score (ligne DB sans analyse ?) — jamais ré-analysés`,
            );
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `addMailsToRecord — qualified sans score`,
                qualifiedWithoutScore.map((mail) => ({
                    internetMessageId: mail.internetMessageId,
                    sentDateTime: mail.sentDateTime,
                    smartLabel: (mail as QualifiedMail).smartLabel,
                })),
            );
        }

        // Call to analyse only BaseMail
        if (baseMailsToProcess.length > 0) {
            // Intentional fire-and-forget : l'analyse IA tourne en arrière-plan.
            // Ne pas awaiter ici — _addMailsToRecord est void et doit rester synchrone.
            // Le Dashboard est déjà affiché ; les mails s'enrichissent via mail-analysis-completed.
            // Si un collecteur est fourni (ex: firstUserEmailsAnalysis), on y pousse la Promise
            // pour permettre un suivi rigoureux de l'analyse complète via Promise.allSettled.
            const analysisPromise = this._processEmailsWithAI(
                baseMailsToProcess,
                forceMailAnalysis,
                batchDelayMs,
            );
            if (analysisPromisesCollector) {
                analysisPromisesCollector.push(analysisPromise);
            }
        }

        return;
    }

    /** Toutes les copies de tous les messages, à plat. */
    public _allCopies(): Mail[] {
        return this.mailRecord.allCopies();
    }

    /** WS4 — clé de slot pour la dédup / le Record : `uniqueMailvistaId` (internetMessageId::folderId). */
    private _copyKey(target: MailCopyFetchTarget): string {
        return uniqueMailvistaId({
            id: target.graphId ?? "",
            internetMessageId: target.internetMessageId,
            parentFolderId: target.folderId,
        });
    }

    // #endregion Record accessors

    /**
     * Détermine la taille d'affichage d'un composant mail.
     *
     * Les PJ inline ne comptent pas : le front ne les affiche pas en vignette, la tuile élargie
     * resterait vide (AB#1863).
     *
     * @param mail L'objet mail à analyser
     * @returns La taille appropriée du composant selon les caractéristiques du mail
     */
    public _determineMailComponentSize(mail: Mail): MailComponentSize {
        const imageTypes = [
            "image/gif",
            "image/jpeg",
            "image/png",
            "image/bmp",
            "image/webp",
            "image/svg+xml",
        ];

        const hasImageAttachments = (mail.attachments ?? []).some(
            (attachment) =>
                imageTypes.includes(attachment.type) &&
                attachment.isInline !== true,
        );

        const hasLinkedEvent = mail.linkedEventDetails !== undefined;

        const hasTodos = (mail.aiDetails?.toDo ?? []).some(
            (toDo) => toDo.isUserAccepted,
        );

        // Règle 1: todo ET (image OU événement lié) => LARGE
        if (hasTodos && (hasImageAttachments || hasLinkedEvent)) {
            return MailComponentSize.LARGE;
        }

        // Règle 2: image OU événement lié => WIDE
        if (hasImageAttachments || hasLinkedEvent) {
            return MailComponentSize.WIDE;
        }

        // Par défaut (todo seul compris) => SMALL
        return MailComponentSize.SMALL;
    }

    /**
     * AB#1900 — éviction sur verdict backend (`notFound` route inbox, `deletedInternetMessageIds` route
     * AnyFolder). Le verdict porte sur le SLOT INBOX interrogé, pas sur le message : on ne retire que
     * cette copie. cf. docs/bugs/ab1900-eviction-copie-vs-message.md
     */
    private _evictInboxCopyOnBackendVerdict(internetMessageId: string): void {
        const inboxFolderId = this.inboxFolderId;
        const inboxCopy = inboxFolderId
            ? this._getCopy(internetMessageId, inboxFolderId)
            : undefined;

        if (inboxCopy && inboxFolderId) {
            // uid capturé AVANT le retrait. WS5c (B) : une notification par copie RETIRÉE —
            // le handler CustomViewManager est déjà copie-aware.
            const inboxUid = uniqueMailvistaId(inboxCopy);
            this._removeCopyFromRecord(internetMessageId, inboxFolderId);
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `  Copie inbox introuvable → retirée du Record - internetMessageId: ${internetMessageId}`,
            );
            this.eventBus.emit("mail-deleted", {
                uniqueMailvistaId: inboxUid,
            });
        } else if (!this.mailsRecordByInternetMessageId[internetMessageId]) {
            // Plus aucune copie : `_removeCopyFromRecord` n'a pas pu purger le view-content.
            this._forgetMailViewContent(internetMessageId);
        }

        // (AB#1645, étendu AB#1900) Purge INCONDITIONNELLE : l'ancien prédicat « clé en
        // Record » la sautait dès qu'une copie sœur était en cache → compteur stale à vie.
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `  Mail sans copie inbox canonique → retrait du store SmartLabel (internetMessageId: ${internetMessageId})`,
        );
        this.smartLabelStore.removeMessage(internetMessageId);
        this.eventBus.emit("navigation-asking-for-update");
    }

    /**
     * @method _fetchAndProcessMails
     * Effectue l'appel API pour récupérer les mails manquants et les traite :
     * fix linkedEventDetails, détection/suppression des mails effacés, ajout au Record.
     *
     * Appelé uniquement depuis fetchMissingMailsIfAnyByInternetMessageIds.
     * Les IDs passés sont déjà vérifiés comme absents du Record.
     *
     * @param missingInternetMessageIds - IDs à fetcher
     * @param forceMailAnalysis         - Si true, force la ré-analyse
     * @param batchDelayMs              - Délai entre les batches d'analyse
     * @param analysisPromisesCollector - [Optionnel] Propagé tel quel jusqu'à _addMailsToRecord.
     *                                   Voir fetchMissingMailsIfAnyByInternetMessageIds pour la description complète.
     */
    private async _fetchAndProcessMails(
        missingInternetMessageIds: string[],
        forceMailAnalysis: boolean,
        batchDelayMs: number,
        analysisPromisesCollector?: Promise<void>[],
    ): Promise<void> {
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `  Fetching ${missingInternetMessageIds.length} missing mails from ApiManager ... `,
        );

        let fetchedMailsFromApi: GetMailsFromInternetMessageIdsResponseBody;
        try {
            // déc. #7 : l'API exige inboxFolderId (400 si absent). Garanti présent ici car _initialize await
            // getGeneralNavBarContent() (→ inbox-folder-id-updated, synchrone) AVANT tout fetch. Garde défensive :
            if (!this.inboxFolderId) {
                throw new Error(
                    "inboxFolderId not available yet — enriched fetch aborted (startup ordering violated)",
                );
            }
            // Call api/v1/mails/getMailsFromInternetMessageIds
            fetchedMailsFromApi =
                await this.apiManager.getMailsByInternetMessageIds(
                    missingInternetMessageIds,
                    this.inboxFolderId,
                );
        } catch (error) {
            logger.error(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `  !!! Error fetching mails from ApiManager: ${error}`,
            );
            // En cas d'erreur de fetch, on ne peut pas distinguer les mails supprimés des erreurs réseau/API
            // On arrête le traitement ici pour éviter de supprimer des mails par erreur
            throw new Error(`Failed to fetch missing mails: ${error.message}`);
        }

        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager, LOGS.ApiManager],
            `  Fetched ${fetchedMailsFromApi.baseMails.length + fetchedMailsFromApi.qualifiedMails.length} Mails`,
        );

        // #region FIX mails (linkedEventDetails + deletedMails )
        // Devra être migré dans Api-Backend car c'est du métier

        // WS7b (Option C) — le contrat R-WS7b-1 reste : ce chemin est INBOX-ONLY (analyse canonique), le
        // Backend (WS1 Lot C, archi hexagonale + tests intégraux) applique `pickCanonicalCopy` STRICTE et
        // renvoie **au plus 1 copie canonique inbox par internetMessageId** (sinon `notFoundMailsInternetMessageIds`).
        let combinedMails: BaseMail[] = [
            ...fetchedMailsFromApi.qualifiedMails,
            ...fetchedMailsFromApi.baseMails,
        ];

        // Observabilité (debug suppression / self-sent) : copie physique retournée par le backend pour
        // chaque internetMessageId (id Graph + parentFolderId). Sert à vérifier que le backend renvoie bien
        // la copie Inbox (canonique stricte) et qu'aucune copie en corbeille ne remonte jusqu'ici.
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `_fetchAndProcessMails - copies retournées par le backend`,
            combinedMails.map((m) => ({
                internetMessageId: m.internetMessageId,
                graphId: m.id,
                parentFolderId: m.parentFolderId,
            })),
        );
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `_fetchAndProcessMails - notFoundMailsInternetMessageIds`,
            fetchedMailsFromApi.notFoundMailsInternetMessageIds,
        );

        // Mails sans verdict : le Backend n'a rien pu conclure à leur sujet. On ne touche PAS à leur Record.
        const unresolvedInternetMessageIds =
            fetchedMailsFromApi.unresolvedMailsInternetMessageIds ?? [];
        if (unresolvedInternetMessageIds.length > 0) {
            logger.warn(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `_fetchAndProcessMails - ${unresolvedInternetMessageIds.length} mail(s) sans verdict provider : Record inchangé, verdict reporté au passage suivant`,
            );
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `_fetchAndProcessMails - unresolvedMailsInternetMessageIds`,
                unresolvedInternetMessageIds,
            );
        }

        // Réduction canonique défensive (régression WS7b) : un message sans copie inbox est OMIS —
        // ne jamais rétablir un repli sur une autre copie (fix P-D). Rend le filet deleted-items
        // ci-dessous inerte ; toute réduction effective = violation R-WS7b-1 du Backend.
        const copiesBeforeDedup = combinedMails;
        combinedMails = dedupToCanonical(combinedMails, {
            inboxFolderId: this.inboxFolderId,
        });
        if (combinedMails.length !== copiesBeforeDedup.length) {
            const keptCopies = new Set(
                combinedMails.map((m) => uniqueMailvistaId(m)),
            );
            const discardedCopies = copiesBeforeDedup.filter(
                (m) => !keptCopies.has(uniqueMailvistaId(m)),
            );
            logger.warn(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `_fetchAndProcessMails — R-WS7b-1 violé par le backend : ${discardedCopies.length} copie(s) non canonique(s) écartée(s) par dedupToCanonical (${copiesBeforeDedup.length} → ${combinedMails.length})`,
            );
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `_fetchAndProcessMails — copies écartées par la dédup canonique`,
                discardedCopies.map((m) => ({
                    internetMessageId: m.internetMessageId,
                    parentFolderId: m.parentFolderId,
                })),
            );
        }

        // #region - Fix linkedEventDetails in case of user decline an event
        // Filter all mails with responseType declined
        const declinedEventMails = combinedMails.filter(
            (mail) => mail.linkedEventDetails?.responseType === "declined",
        );

        declinedEventMails.forEach((declinedEventMail: BaseMail) => {
            // Get all mail in same thread from record and fetchedMissingGraphMails
            const threadMailsFromRecord: Mail[] = this._allCopies().filter(
                (mail) =>
                    mail.conversationId === declinedEventMail.conversationId,
            );

            const threadMailsFromFetched: BaseMail[] = combinedMails.filter(
                (mail) =>
                    mail.conversationId === declinedEventMail.conversationId,
            );

            // Concate all thread (even declinedEventMail)
            const allThreadMails: BaseMail[] = [
                ...threadMailsFromRecord,
                ...threadMailsFromFetched,
            ];

            // Clear linkedEventDetails for all mails in the thread
            allThreadMails.forEach((mail) => {
                // Check if mail has linkedEventDetails and if internetMessageId different from internetMessageId of declinedEventMail.
                if (
                    mail.linkedEventDetails &&
                    mail.internetMessageId !==
                        declinedEventMail.internetMessageId &&
                    mail.linkedEventDetails?.messageType !== "Request" // Update linkedEventDetails only for event request
                ) {
                    mail.linkedEventDetails = undefined;
                    mail.hasLinkedMeeting = false;
                    mail.componentSize = this._determineMailComponentSize(
                        mail as Mail,
                    ); // Update size of component for front

                    // Émettre l'événement de mise à jour
                    this.eventBus.emit("mail-updated", mail as Mail);
                }
            });

            // Set userResponseStatus to declined on event declined
            declinedEventMail.linkedEventDetails!.userResponseStatus =
                AttendeeResponseStatus.DECLINED;
        });

        // #endregion - Fix linkedEventDetails in case of user decline an event

        // #region - MECANISME DE REPERAGE DE MAIL SUPPRIMES (Cas Smart Label)
        // WS7b — FILET DÉFENSIF (defense-in-depth). Sous le contrat inbox-only canonique (WS1 Lot C/D),
        // l'API ne renvoie jamais de copie Deleted Items ici (canonique-null → notFound + purge Backend),
        // et la dédup canonique en amont rend ce bloc inerte par construction.
        const deletedItemsInternetMessageIds = combinedMails
            .filter((mail) => {
                return mail.parentFolderId === this.deleteditemsFolderId;
            })
            .map((mail) => mail.internetMessageId);

        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `#### fetchMissingMailsIfAnyByInternetMessageIds - deletedItemsInternetMessageIds :`,
            deletedItemsInternetMessageIds,
        );

        // Liste les internetMessagesIds non trouve dans la DB et dans le Provider (Graph ou Google)
        const internetMessageIdsNotFoundFromProviderAndDb =
            fetchedMailsFromApi.notFoundMailsInternetMessageIds;
        if (internetMessageIdsNotFoundFromProviderAndDb.length > 0) {
            logger.warn(
                [LOGS.MailManager],
                `internetMessageIds not found from DB and Provider : ${internetMessageIdsNotFoundFromProviderAndDb}`,
            );
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `#### fetchMissingMailsIfAnyByInternetMessageIds - internetMessageIdsNotFoundFromProviderAndDB :`,
                internetMessageIdsNotFoundFromProviderAndDb,
            );
        }

        // Supprime du Record tous les mails non trouvé en DB et dans le Provider (Graph ou Google)
        internetMessageIdsNotFoundFromProviderAndDb.forEach(
            (internetMessageId) =>
                this._evictInboxCopyOnBackendVerdict(internetMessageId),
        );

        // Tous mails dans DeletedItems dans Graph ne doivent plus exister en DB
        if (deletedItemsInternetMessageIds.length > 0) {
            // [WS-C / AB#1576] migré Core → Api-Backend
            const nbObsoleteMailsDeleted =
                await this.apiManager.deleteAnalyzedMailsByInternetMessageIds(
                    deletedItemsInternetMessageIds,
                );
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                ` ${nbObsoleteMailsDeleted} Mail(s) supprimé(s) de la DB`,
            );

            const deletedItemsSet = new Set(deletedItemsInternetMessageIds);

            const deletedMailObjects = combinedMails.filter((m) =>
                deletedItemsSet.has(m.internetMessageId),
            );

            // Notifier CustomViewManager — retire ces copies de toutes les smart label views
            // WS5c (B) : payload copie-aware (copie Deleted Items réellement présente côté provider).
            deletedMailObjects.forEach((deletedMail) => {
                this.eventBus.emit("mail-deleted", {
                    uniqueMailvistaId: uniqueMailvistaId(deletedMail),
                });
            });

            // Exclure de combinedMails pour ne pas déclencher d'analyse IA
            combinedMails = combinedMails.filter(
                (m) => !deletedItemsSet.has(m.internetMessageId),
            );

            // Placer dans le Record en état "nu" pour permettre l'affichage dans le dossier Supprimés
            for (const baseMail of deletedMailObjects) {
                if (
                    !this.mailsRecordByInternetMessageId[
                        baseMail.internetMessageId
                    ]
                ) {
                    // Mail pas encore dans le Record : l'insérer avec les champs Mail obligatoires
                    this._setMailInRecord({
                        ...baseMail,
                        isFocus: false,
                        isUserProcessed: false,
                        isUserEdited: false,
                        score: null,
                        manualFocusDate: null,
                        sentDateTimeMailvista: null,
                    } as Mail);
                }
                // Nettoyer les champs d'analyse (isFocus, smartLabel, aiDetails…)
                this._updateRecordWithoutAnalysis(
                    baseMail.id,
                    baseMail.internetMessageId,
                );
            }
        }

        // #endregion - MECANISME DE REPERAGE DE MAIL SUPPRIMES (Cas Smart Label)

        // #endregion FIX mails (linkedEventDetails + deletedMails )

        logger.info([LOGS.TESTS], `@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@`);
        logger.info(
            [LOGS.TESTS],
            `  Adding to record newly graph fetched mails`,
            fetchedMailsFromApi.baseMails,
        );
        logger.info([LOGS.TESTS], `@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@`);

        // Ajout des des mails manquants au Record
        this._addMailsToRecordAndLaunchAnalysisForNew(
            combinedMails,
            forceMailAnalysis,
            batchDelayMs,
            analysisPromisesCollector,
        );
    }

    /**
     * Fetch + peuplement du Record pour UN lot d'internetMessageIds. Best-effort : un échec de lot ne
     * peuple rien pour ce lot (retry autorisé au prochain appel) et n'impacte pas les autres lots.
     * Les ids absents de la réponse sont marqués « fetchés sans contenu » (bundle vide) pour ne pas
     * être redemandés.
     */
    private async _fetchMailViewContentChunk(
        internetMessageIds: string[],
    ): Promise<void> {
        try {
            const chunkStart = performance.now();
            logger.info(
                LOGS.AB1727,
                `[AB#1727-SDK] POST /mails/content chunk: requesting ${internetMessageIds.length} mail(s)`,
            );
            const entries =
                await this.apiManager.getMailsViewContentByInternetMessageIds(
                    internetMessageIds,
                );
            logger.perf(
                LOGS.CustomViewManager,
                `[timing] view-content batch (${internetMessageIds.length} mails)`,
                chunkStart,
            );
            const byId = new Map<string, MailViewContent>();
            for (const entry of entries) {
                byId.set(entry.internetMessageId, {
                    attachments: entry.attachments ?? [],
                    links: entry.links ?? [],
                    events: entry.events ?? [],
                });
            }
            for (const imid of internetMessageIds) {
                this.mailViewContentRecordByInternetMessageId[imid] = byId.get(
                    imid,
                ) ?? { attachments: [], links: [], events: [] };
            }
            const withContent = internetMessageIds.filter((id) =>
                byId.has(id),
            ).length;
            logger.info(
                LOGS.AB1727,
                `[AB#1727-SDK] POST /mails/content chunk done: requested=${internetMessageIds.length} entriesReturned=${entries.length} withContent=${withContent} emptyMarked=${internetMessageIds.length - withContent}`,
            );
        } catch (error) {
            logger.error(
                [LOGS.MailManager],
                `_fetchMailViewContentChunk failed (${internetMessageIds.length} mails): ${error}`,
            );
            // Ne rien peupler pour ce lot → retry autorisé au prochain appel.
        }
    }

    /**
     * WS4 — Moteur de fetch commun à tous les chemins. Ne connaît QUE la mécanique transverse : dédup
     * des appels concurrents keyée par slot, retry « 1 fois », fail graceful. Ce qui varie est porté
     * par la `strategy` — aucun test sur le chemin ici.
     */
    private async _fetchMissingCopies(
        targets: MailCopyFetchTarget[],
        strategy: MailCopyFetchStrategy,
        askedBy: string,
        _remainingRetries: number = 1,
    ): Promise<void> {
        const missingTargets = this._findMissingCopies(
            targets,
            strategy.isMissing,
        );

        if (missingTargets.length === 0) {
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `  No copy to fetch (${strategy.path}), everything already in Record.`,
            );
            return;
        }

        // --- Deduplication layer (keyée uniqueMailvistaId / slot) ---
        type InFlightEntry = {
            targets: MailCopyFetchTarget[];
            promise: Promise<void>;
        };
        const inFlightEntries: InFlightEntry[] = [];
        const toFetch: MailCopyFetchTarget[] = [];

        for (const target of missingTargets) {
            const key = this._copyKey(target);
            const existingPromise = this._inFlightFetches.get(key);
            if (existingPromise) {
                const existing = inFlightEntries.find(
                    (e) => e.promise === existingPromise,
                );
                if (existing) {
                    existing.targets.push(target);
                } else {
                    inFlightEntries.push({
                        targets: [target],
                        promise: existingPromise,
                    });
                }
            } else {
                toFetch.push(target);
            }
        }

        // Create own fetch promise for new slots (if any) — dispatch selon le chemin
        let ownEntry: InFlightEntry | null = null;
        if (toFetch.length > 0) {
            const ownPromise = strategy.fetch(toFetch);

            const keys = toFetch.map((t) => this._copyKey(t));
            for (const key of keys) {
                this._inFlightFetches.set(key, ownPromise);
            }
            // L'échec de `ownPromise` est traité plus bas (allSettled) : la promesse dérivée par
            // `finally` ne doit pas le relever une seconde fois, sans personne pour l'attraper.
            ownPromise
                .finally(() => {
                    for (const key of keys) {
                        this._inFlightFetches.delete(key);
                    }
                })
                .catch(() => undefined);

            ownEntry = { targets: toFetch, promise: ownPromise };
        }

        // Await all (inFlight + own) with allSettled — never throws
        const allEntries: InFlightEntry[] = [
            ...inFlightEntries,
            ...(ownEntry ? [ownEntry] : []),
        ];
        const results = await Promise.allSettled(
            allEntries.map((e) => e.promise),
        );

        // Collect failed targets from rejected promises
        const failedTargets: MailCopyFetchTarget[] = [];
        results.forEach((result, index) => {
            if (result.status === "rejected") {
                logger.warn(
                    [LOGS.MailManager],
                    `fetchMissingCopies ${askedBy} — Promise rejetée (${allEntries[index].targets.length} slot(s)) : ${result.reason}`,
                );
                failedTargets.push(...allEntries[index].targets);
            }
        });

        // Retry failed slots once (dedup applies to the retry too — protects against retry storm)
        if (failedTargets.length > 0) {
            if (_remainingRetries > 0) {
                logger.info(
                    [LOGS.MailManager],
                    `fetchMissingCopies ${askedBy} — retry de ${failedTargets.length} slot(s) (_remainingRetries=${_remainingRetries})`,
                );
                try {
                    await this._fetchMissingCopies(
                        failedTargets,
                        strategy,
                        `${askedBy} [retry]`,
                        _remainingRetries - 1,
                    );
                } catch (retryError) {
                    logger.error(
                        [LOGS.MailManager],
                        `fetchMissingCopies ${askedBy} — retry échoué : ${retryError}`,
                    );
                    // Fail gracefully : les copies manquantes ne seront pas dans le Record pour cet appel
                }
            } else {
                logger.warn(
                    [LOGS.MailManager],
                    `fetchMissingCopies ${askedBy} — retries épuisés, ${failedTargets.length} slot(s) non récupérés (fail graceful)`,
                );
            }
        }
    }

    /**
     * WS4 — Dédoublonne les cibles par `uniqueMailvistaId` (slot) et ne garde que les manquantes, selon
     * le critère du chemin appelant (cf. `_isSlotMissing` / `_isMessageUnknown`).
     */
    private _findMissingCopies(
        targets: MailCopyFetchTarget[],
        isMissing: (target: MailCopyFetchTarget) => boolean,
    ): MailCopyFetchTarget[] {
        if (!targets || targets.length === 0) {
            return [];
        }
        const seen = new Set<string>();
        const missing: MailCopyFetchTarget[] = [];
        for (const target of targets) {
            const key = this._copyKey(target);
            if (seen.has(key)) continue;
            seen.add(key);
            if (isMissing(target)) {
                missing.push(target);
            }
        }
        if (missing.length > 0) {
            logger.info(
                [LOGS.MailManager],
                `Found ${missing.length} missing copies (slots) in Record`,
            );
        }
        return missing;
    }

    // #region Record accessors (WS3 — structure multi-copies)

    /** Contexte dossiers pour la sélection canonique (seul inboxFolderId nécessaire en règle stricte). */
    private _folderContext(): MailboxFolderContext {
        return { inboxFolderId: this.inboxFolderId };
    }

    /**
     * Purge la clé d'une copie dans _attachmentsFetchedSet. À appeler chaque fois qu'une copie est
     * retirée de mailsRecordByInternetMessageId, pour autoriser un réenrichissement si cette copie
     * est recréée par un refetch ultérieur (sinon le guard de getMailFullBodyContentWithAttachments
     * bloque pour toujours le nouvel objet, jamais enrichi, avec la même clé). Cf. #1332.
     */
    private _forgetAttachmentsFetched(
        copyKeyOrMail: string | MailCopyRef,
    ): void {
        const key =
            typeof copyKeyOrMail === "string"
                ? copyKeyOrMail
                : uniqueMailvistaId(copyKeyOrMail);
        this._attachmentsFetchedSet.delete(key);

        // Purge aussi les previews de CETTE copie (clés `${uniqueMailvistaId}::${attachmentId}`). Copy-scoped,
        // donc branché ici : `_forgetAttachmentsFetched` est déjà appelé à chaque retrait/re-key de copie
        // (move corbeille, suppression définitive, éviction notFound). Un move re-key la copie → les previews
        // de l'ancien dossier deviennent orphelines et sont retirées ici (la nouvelle copie re-fetchera à la demande).
        const previewPrefix = `${key}::`;
        for (const previewKey of Object.keys(
            this.attachmentPreviewsRecordByKey,
        )) {
            if (previewKey.startsWith(previewPrefix))
                delete this.attachmentPreviewsRecordByKey[previewKey];
        }
    }

    /**
     * Purge le contenu « widgets » (attachments/links/events) d'un MESSAGE du Record unifié.
     * À appeler UNIQUEMENT quand le message disparaît TOTALEMENT du Record (dernière copie retirée,
     * éviction notFound) — PAS sur un simple move corbeille : le mail reste accessible via Supprimés,
     * et de toute façon il quitte la membership des CustomViews (donc son view-content n'est plus requêté).
     */
    private _forgetMailViewContent(internetMessageId: string): void {
        delete this.mailViewContentRecordByInternetMessageId[internetMessageId];
        this._inFlightViewContentFetches.delete(internetMessageId);
    }

    /**
     * Copie représentative d'un message : copie inbox canonique si disponible,
     * sinon n'importe quelle copie présente (non-régression mono-copie).
     * NB : la sélection stricte inbox-only des vues SmartLabel est durcie en WS4/WS6.
     */
    public _getCanonicalMail(internetMessageId: string): Mail | undefined {
        const copies = this.mailsRecordByInternetMessageId[internetMessageId];
        if (!copies) return undefined;
        const canonical = pickCanonicalCopy(
            [...copies.values()],
            this._folderContext(),
        );
        return canonical ?? copies.values().next().value;
    }

    /** WS5c-2 — Copies SURVIVANTES d'un message présentes dans le Record (toutes ses copies restantes).
     *  À appeler depuis le handler `mail-deleted` APRÈS retrait de la copie supprimée → permet la
     *  réévaluation CustomView conditionnelle (`_isMailMatchCustomView` sur une copie survivante, §2.4). */
    public _getCopiesForMessage(internetMessageId: string): Mail[] {
        const copies = this.mailsRecordByInternetMessageId[internetMessageId];
        return copies ? [...copies.values()] : [];
    }

    /** Accès à une copie précise par slot (internetMessageId, parentFolderId). */
    public _getCopy(
        internetMessageId: string,
        parentFolderId: string,
    ): Mail | undefined {
        return this.mailsRecordByInternetMessageId[internetMessageId]?.get(
            parentFolderId,
        );
    }

    public _getDeletedItemsFolderId(): string | undefined {
        return this.deleteditemsFolderId;
    }

    /** WS5c-2 — Id du dossier Inbox (canonique). Permet au handler `mail-deleted` de décider
     *  si la copie supprimée était la copie inbox (retrait SmartLabel conditionnel, §2.4). */
    public _getInboxFolderId(): string | undefined {
        return this.inboxFolderId;
    }

    /**
     * AB#1621 — Lit la fenêtre rétrospective d'analyse (byMail / byDay) depuis la licence active.
     * Repli sûr sur les constantes (`INITIAL_ANALYSIS_MAIL_COUNT` / `INITIAL_ANALYSIS_BY_DAY_DEFAULT`)
     * si la licence est indisponible ou porte des valeurs aberrantes (≤ 0) — on ne dérive JAMAIS un
     * budget/plancher d'un 0 (cf. INVESTIGATION §5.0/§5.6 cas 5).
     */
    public _getInitialAnalysisLimits(): { byMail: number; byDay: number } {
        const initialAnalysis =
            this.licenseManager.getLicenseProfile()?.[0]?.detail
                ?.initialAnalysis;
        const byMail =
            initialAnalysis && initialAnalysis.byMail > 0
                ? initialAnalysis.byMail
                : INITIAL_ANALYSIS_MAIL_COUNT;
        const byDay =
            initialAnalysis && initialAnalysis.byDay > 0
                ? initialAnalysis.byDay
                : INITIAL_ANALYSIS_BY_DAY_DEFAULT;
        return { byMail, byDay };
    }

    public _getMailFromRecordById(internetMessageId: string): Mail | null {
        // TODO - Check if mail not found we should call Grapha and DB ?
        const mail = this._getCanonicalMail(internetMessageId);

        if (!mail) {
            logger.warn(
                [LOGS.MailManager],
                `Mail avec internetMessageId ${internetMessageId} introuvable dans le Record`,
            );

            // TEST To fetch mail if missing to avoid errors !
            // NEED TO CHECK IMPACTS on startup en potential parallel fetchs !!!
            // await this.fetchMissingMailsIfAnyByInternetMessageIds([internetMessageId]);

            return null;
        }

        return mail;
    }

    // #endregion Mail content management

    // #region Utils

    /**
     * @method isInboxFolder
     * Vérifie si l'ID de dossier fourni correspond au dossier inbox
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} parentFolderId - ID du dossier à vérifier
     * @output {boolean} True si le dossier est l'inbox, false sinon
     * @private
     */
    public _isInboxFolder(parentFolderId: string): boolean {
        if (!this.inboxFolderId) {
            logger.error(
                [],
                "_isInboxFolder called before inboxFolderId is initialized. Should never happen => check startup lifecycle !",
            );
        }
        return parentFolderId === this.inboxFolderId;
    }

    /** Chemins ciblés (canonique, vue Dossier) : manquant = le slot visé est absent du Record. */
    private _isSlotMissing = (target: MailCopyFetchTarget): boolean =>
        !this.mailsRecordByInternetMessageId[target.internetMessageId]?.get(
            target.folderId,
        );

    // #region Mail content management

    /** Analyses IA en cours au moment de l'appel, lancées par n'importe quel appelant. */
    public getRunningAnalysisPasses(): Promise<void>[] {
        return [...this._runningAnalysisPasses];
    }

    /**
     * @method processEmailsWithAI
     * Traite un lot d'emails avec l'analyse IA ; l'analyse est suivie jusqu'à sa fin (`getRunningAnalysisPasses`).
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {Mail[]} emails - Liste des emails à analyser
     * @output {Promise<void>}
     * @private
     */
    public _processEmailsWithAI(
        emails: BaseMail[],
        forceMailAnalysis: boolean = false,
        batchDelayMs: number = 2000,
    ): Promise<void> {
        const analysis = this._runEmailsAnalysis(
            emails,
            forceMailAnalysis,
            batchDelayMs,
        );
        this._runningAnalysisPasses.add(analysis);
        const untrack = () => {
            this._runningAnalysisPasses.delete(analysis);
        };
        analysis.then(untrack, untrack);

        return analysis;
    }

    private async _runEmailsAnalysis(
        emails: BaseMail[],
        forceMailAnalysis: boolean,
        batchDelayMs: number,
    ): Promise<void> {
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `processEmailsWithAI (${emails.length} mails)`,
        );

        // Configuration du batch processing
        const BATCH_SIZE = 30; // Nombre de mails par batch
        const BATCH_DELAY_MS = batchDelayMs; // Délai entre les batchs en millisecondes (5 secondes par défaut)

        // Tri chronologique : analyser les mails du plus ancien au plus récent
        emails.sort(
            (a, b) =>
                new Date(a.sentDateTime!).getTime() -
                new Date(b.sentDateTime!).getTime(),
        );

        // Quota restant, appliqué après le pré-filtre. `null` = pas de plafond connu.
        let remainingAnalysisQuota: number | null = null;

        // Si forceMailAnalysis est true, on bypass la vérification de licence
        if (!forceMailAnalysis) {
            // TODO - Implement grace delay !
            if (
                !this.featureFlags.isEnabled(FeatureFlag.MAIL_ENABLE_ANALYSIS)
            ) {
                logger.error(
                    [LOGS.MailManager],
                    `  !!! Feature flag MAIL_ENABLE_ANALYSIS is not enabled — analysis not permitted !!!`,
                );
                this.eventBus.emit("no-valid-license-for-analysis");
                return;
            }

            // Le compteur local a pu être posé il y a des heures, ou consommé par un autre client.
            await this._refreshQuotaState();

            // Vérification du quota mensuel
            if (
                this.analysisState.currentMonthEmailCount !== null &&
                this.analysisState.monthlyAnalysedMailsLimit !== null
            ) {
                // Vérifier si l'ajout des nouveaux emails dépasserait la limite
                const remainingQuota =
                    this.analysisState.monthlyAnalysedMailsLimit -
                    this.analysisState.currentMonthEmailCount;

                if (remainingQuota <= 0) {
                    this.eventBus.emit("max-analysed-mails-reached");
                    logger.error(
                        [LOGS.MailManager],
                        `  !!! Monthly email analysis limit reached (${this.analysisState.currentMonthEmailCount}/${this.analysisState.monthlyAnalysedMailsLimit}) !!!`,
                    );
                    return;
                }

                // Tronquer ici, avant le pré-filtre, laisserait les mails écartés manger la fenêtre.
                remainingAnalysisQuota = remainingQuota;
            } else {
                logger.warn(
                    [LOGS.MailManager],
                    `  Unable to check monthly limit (currentCount: ${this.analysisState.currentMonthEmailCount}, limit: ${this.analysisState.monthlyAnalysedMailsLimit})`,
                );
            }
        } else {
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `  License check bypassed due to forceMailAnalysis = true`,
            );
        }

        // Filter emails to analyse : only after first licence date
        let analysableEmails: BaseMail[];

        // Le filtre inbox est une règle métier absolue : seuls les mails de la boîte de réception
        // sont analysés, même lorsque forceMailAnalysis est true (qui contourne uniquement la licence et la date).
        if (forceMailAnalysis) {
            analysableEmails = emails.filter((email) =>
                this._isInboxFolder(email.parentFolderId),
            );
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `  ${analysableEmails.length}/${emails.length} inbox mails will be analyzed (forceMailAnalysis = true — licence & date bypassed, inbox filter enforced)`,
            );
        }
        // Sinon, filtrage basé sur les conditions de date classiques
        else {
            let userProfile = this.profileLoader.cachedProfile;
            let registrationDateValue = userProfile?.profile?.registrationDate;

            if (!registrationDateValue) {
                logger.warn(
                    LOGS.MailManager,
                    "User has no registration date, impossible to filter before analysis !!!",
                );
                logger.info(LOGS.MailManager, "userProfile", userProfile);

                logger.warn(
                    LOGS.MailManager,
                    "MailManager fetching manually userProfile, should not happen if processing was done AFTER profile is fetched",
                );
                userProfile = await this.profileLoader.getUserProfile();
                logger.info(
                    LOGS.MailManager,
                    "newly fetched userProfile",
                    userProfile,
                );

                registrationDateValue = userProfile?.profile?.registrationDate;
            }
            if (!registrationDateValue) {
                logger.error(
                    LOGS.MailManager,
                    "User has no registration date, impossible to filter before analysis !!!",
                );
                return;
            }
            const registrationDate = new Date(registrationDateValue);
            // AB#1621 — Pré-filtre d'OPTIMISATION (l'API reste l'autorité, défense en profondeur comme inbox-only) :
            // borne sur le plancher figé `analysisFloorDate` s'il est posé, sinon repli sur le plancher
            // provisoire `registration − byDay` (byDay licence). Remplace l'ancien `registration − 15 j` hardcodé.
            const { byDay } = this._getInitialAnalysisLimits();
            const analysisFloorValue = userProfile?.profile?.analysisFloorDate;
            const limitDate = analysisFloorValue
                ? new Date(analysisFloorValue)
                : new Date(
                      registrationDate.getTime() - byDay * 24 * 60 * 60 * 1000,
                  );

            analysableEmails = emails.filter((email) => {
                const isInbox = this._isInboxFolder(email.parentFolderId);
                const sentDate = new Date(email.sentDateTime!);

                const isAnalysable = isInbox && sentDate >= limitDate;

                return isAnalysable;
            });
        }

        if (
            remainingAnalysisQuota !== null &&
            analysableEmails.length > remainingAnalysisQuota
        ) {
            logger.warn(
                [LOGS.MailManager],
                `  !!! Only analyzing ${remainingAnalysisQuota} of ${analysableEmails.length} emails due to monthly limit !!!`,
            );
            analysableEmails = analysableEmails.slice(
                0,
                remainingAnalysisQuota,
            );
        }

        // Skip attendu (hors-inbox / plancher), tracé car longtemps silencieux.
        const preFilteredOutCount = emails.length - analysableEmails.length;
        if (preFilteredOutCount > 0) {
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `  Pré-filtre analyse : ${preFilteredOutCount}/${emails.length} mail(s) écartés (hors-inbox ou antérieurs au plancher)`,
            );
        }

        // Fonction helper pour créer un délai
        const delay = (ms: number) =>
            new Promise((resolve) => setTimeout(resolve, ms));

        // déc. #7 : analysis-save-mail exige `inboxFolderId` (400 si absent) pour l'analyse inbox-aware.
        // L'analyse est inbox-only et inboxFolderId est garanti présent post-startup ; garde défensive
        // (pas d'analyse sans contexte → évite une rafale de 400). Déploiement conjoint OBLIGATOIRE avec le Backend.
        const inboxFolderId = this.inboxFolderId;
        if (!inboxFolderId) {
            logger.error(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                `_processEmailsWithAI — inboxFolderId pas encore prêt, analyse abandonnée (déc. #7)`,
            );
            return;
        }

        // Traitement par batches
        const totalBatches = Math.ceil(analysableEmails.length / BATCH_SIZE);
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `  Processing ${analysableEmails.length} emails in ${totalBatches} batch(es) of ${BATCH_SIZE} emails max`,
        );

        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
            if (this.disposed) return;

            const startIdx = batchIndex * BATCH_SIZE;
            const endIdx = Math.min(
                startIdx + BATCH_SIZE,
                analysableEmails.length,
            );
            const batchEmails = analysableEmails.slice(startIdx, endIdx);

            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `  Batch ${batchIndex + 1}/${totalBatches}: Processing ${batchEmails.length} emails (${startIdx + 1} to ${endIdx})`,
            );

            // Create promises for each email analysis in the batch
            const analysisPromises = batchEmails.map(
                async (email: BaseMail) => {
                    // AB#1897 - Dedup des analyses en vol : une analyse deja lancee pour ce mail rend
                    // la seconde inutile (le backend refuse de la persister) et couteuse (IA + quota).
                    const inFlightAnalysis = this._inFlightAnalyses.get(
                        email.internetMessageId,
                    );
                    if (inFlightAnalysis) {
                        logger.info(
                            [LOGS.MailManager, LOGS.AnalysisLifecycle],
                            `  Analyse deja en vol pour ${email.internetMessageId} - appel dedupe`,
                        );
                        // On attend l'analyse en cours pour conserver le rythme du batch, mais on ne
                        // reemet rien et on ne recompte rien : l'appel d'origine s'en charge et traite
                        // son propre echec.
                        await inFlightAnalysis.catch(() => undefined);
                        return null;
                    }

                    try {
                        // Processing mail with AI (déc. #7 : inboxFolderId requis par l'API)
                        const ownAnalysis = this.apiManager.analyseAndSaveMail(
                            email,
                            inboxFolderId,
                        );
                        this._inFlightAnalyses.set(
                            email.internetMessageId,
                            ownAnalysis,
                        );

                        const result = await ownAnalysis;

                        this.eventBus.emit("mail-analysis-completed", result);

                        return result;
                    } catch (error) {
                        const errorMessage =
                            error instanceof Error
                                ? error.message
                                : String(error);

                        if (
                            errorMessage.includes(
                                "MONTHLY_ANALYSIS_QUOTA_EXCEEDED",
                            )
                        ) {
                            logger.error(
                                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                                `Monthly quota exceeded on server side — stopping batch processing`,
                            );
                            this.eventBus.emit("max-analysed-mails-reached");
                            throw error;
                        }
                        if (
                            errorMessage.includes(
                                "NO_VALID_LICENSE_FOR_ANALYSIS",
                            )
                        ) {
                            logger.error(
                                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                                `License error — stopping batch processing`,
                            );
                            this.eventBus.emit("no-valid-license-for-analysis");
                            throw error;
                        }
                        if (errorMessage.includes("OUTSIDE_ANALYSIS_WINDOW")) {
                            // AB#1621 — Mail antérieur au plancher rétrospectif : rejet API ATTENDU (défense en profondeur,
                            // le pré-filtre SDK aurait normalement déjà écarté ce mail). On l'ignore proprement et on
                            // CONTINUE le batch (ni event d'erreur, ni re-throw) — contrairement à licence/quota.
                            return null;
                        }

                        logger.error(
                            [LOGS.MailManager, LOGS.AnalysisLifecycle],
                            `Error processing email ${email.internetMessageId} with AI: ${error}`,
                        );
                        // En cas d'erreur, on ne supprime pas le mail de la liste pour le réessayer plus tard
                        throw error;
                    } finally {
                        // Liberation sur TOUS les chemins (succes, skip fenetre, erreur) : un mail dont
                        // l'analyse a echoue doit pouvoir etre rejoue au passage suivant.
                        this._inFlightAnalyses.delete(email.internetMessageId);
                    }
                },
            );

            // Launch awaited analysis for this batch
            try {
                await Promise.all(analysisPromises);
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : String(error);
                // Si erreur licence ou quota, arrêter les batchs suivants
                if (
                    errorMessage.includes("MONTHLY_ANALYSIS_QUOTA_EXCEEDED") ||
                    errorMessage.includes("NO_VALID_LICENSE_FOR_ANALYSIS")
                ) {
                    logger.info(
                        [LOGS.AnalysisLifecycle, LOGS.MailManager],
                        `  Stopping all batches due to license/quota error`,
                    );
                    return;
                }
            }

            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `  Batch ${batchIndex + 1}/${totalBatches} completed. Current monthly count: ${this.analysisState.currentMonthEmailCount}/${this.analysisState.monthlyAnalysedMailsLimit || "unlimited"}`,
            );

            // Attendre avant le prochain batch (sauf pour le dernier)
            if (batchIndex < totalBatches - 1) {
                logger.info(
                    [LOGS.AnalysisLifecycle, LOGS.MailManager],
                    `  Waiting ${BATCH_DELAY_MS / 1000} seconds before next batch...`,
                );
                await delay(BATCH_DELAY_MS);
            }
        }

        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `  All batches processing done. Final monthly count: ${this.analysisState.currentMonthEmailCount}/${this.analysisState.monthlyAnalysedMailsLimit || "unlimited"}`,
        );

        // Le lot étant tronqué au quota, le serveur ne rejette jamais : ce contrôle est le seul à lever.
        if (!forceMailAnalysis && analysableEmails.length > 0) {
            await this._refreshQuotaState();
        }

        if (
            !forceMailAnalysis &&
            this.analysisState.currentMonthEmailCount !== null &&
            this.analysisState.monthlyAnalysedMailsLimit !== null &&
            this.analysisState.currentMonthEmailCount >=
                this.analysisState.monthlyAnalysedMailsLimit
        ) {
            logger.warn(
                [LOGS.MailManager, LOGS.AnalysisLifecycle],
                `  Monthly analysis quota reached (${this.analysisState.currentMonthEmailCount}/${this.analysisState.monthlyAnalysedMailsLimit})`,
            );
            this.eventBus.emit("max-analysed-mails-reached");
        }
    }

    // Le backend incrémente le compteur lui-même : deux clients ouverts ne peuvent pas le tenir chacun.
    // Le flag en dérive, donc il se recalcule ici : ailleurs, les deux divergent à nouveau.
    public _refreshQuotaState(): Promise<void> {
        if (this._quotaStateRefresh) return this._quotaStateRefresh;

        const refresh = (async () => {
            try {
                const profile = await this.apiManager.getUserProfile();
                // Cast non validé : un champ absent passerait le `!== null` et rendrait le quota NaN.
                if (typeof profile?.monthlyAnalyzedMailCount === "number") {
                    this.analysisState.currentMonthEmailCount =
                        profile.monthlyAnalyzedMailCount;
                    await this.mailAnalysisFlag.refresh(
                        () => this.analysisState.currentMonthEmailCount,
                    );
                } else {
                    logger.warn(
                        [LOGS.MailManager, LOGS.AnalysisLifecycle],
                        `Monthly count absent from profile, keeping ${this.analysisState.currentMonthEmailCount}`,
                    );
                }
            } catch (error) {
                logger.warn(
                    [LOGS.MailManager, LOGS.AnalysisLifecycle],
                    `Quota state refresh failed, keeping ${this.analysisState.currentMonthEmailCount}: ${error}`,
                );
            } finally {
                this._quotaStateRefresh = null;
            }
        })();

        this._quotaStateRefresh = refresh;

        return refresh;
    }

    /**
     * WS5b — Retire UNE copie (slot) du Record. Si c'était la dernière copie du message,
     * l'entrée du message disparaît entièrement. (Suppression copie-exacte, cf. 05 §2.4.1.)
     */
    public _removeCopyFromRecord(
        internetMessageId: string,
        parentFolderId: string,
    ): void {
        const copies = this.mailsRecordByInternetMessageId[internetMessageId];
        if (!copies) return;
        copies.delete(parentFolderId);
        this._forgetAttachmentsFetched({
            id: "",
            internetMessageId,
            parentFolderId,
        });
        if (copies.size === 0) {
            delete this.mailsRecordByInternetMessageId[internetMessageId];
            // Dernière copie retirée ⇒ message totalement absent du Record → purge son view-content.
            this._forgetMailViewContent(internetMessageId);
        }
    }

    public async _searchViewFilterResults(
        viewFilter: ViewFilter,
        limit: number | undefined = undefined,
    ): Promise<DataSearchResult[]> {
        logger.info(LOGS.MailManager, "getViewFilterInternetMessageIds");

        // ═══════════════════════════════════════════════════════════════════════════
        // VALIDATION DES PARAMETRES
        // ═══════════════════════════════════════════════════════════════════════════
        if (!viewFilter) {
            logger.error(LOGS.MailManager, "No custom view filter provided");
            throw new Error("No custom view filter provided");
        }
        logger.info(LOGS.MailManager, `    viewFilter`, viewFilter);

        // ═══════════════════════════════════════════════════════════════════════════
        // DETECTION DU TYPE DE RECHERCHE
        // ═══════════════════════════════════════════════════════════════════════════
        // TODO - Implémenter les filtres de keyword en DB sans casser l'intersect avec Graph
        const hasAIProperties =
            (viewFilter.categories && viewFilter.categories.length > 0) ||
            (viewFilter.mailStatus && viewFilter.mailStatus.length > 0);
        // TODO - Implement Keywords search in DB
        // || (viewFilter.keywords   && viewFilter.keywords.length   > 0)
        const hasNativeProperties = !!(
            viewFilter.keywords?.length ||
            viewFilter.startDate ||
            viewFilter.endDate ||
            viewFilter.identifiedSenders?.length ||
            viewFilter.identifiedRecipients?.length ||
            viewFilter.readStatus != undefined ||
            viewFilter.hasAttachment != undefined
        );

        let viewFilterSearchResults: DataSearchResult[] = [];

        // ═══════════════════════════════════════════════════════════════════════════
        // CAS 1: RECHERCHE UNIQUEMENT NATIVE
        // ═══════════════════════════════════════════════════════════════════════════
        if (!hasAIProperties && hasNativeProperties) {
            logger.info(LOGS.MailManager, "Recherche uniquement native");
            try {
                viewFilterSearchResults =
                    await this.dataManager.searchInternetMessageIdsFromViewFilter(
                        viewFilter,
                        limit,
                    );
            } catch (error) {
                // 429 déjà géré exhaustivement par le backoff de callGraphEndPoint → ne pas rejouer
                if (isThrottleError(error)) throw error;
                logger.error(
                    [LOGS.MailManager],
                    `Error in getViewFilterInternetMessageIds during searchInternetMessageIdsFromViewFilter : ${error}`,
                );
                logger.warn(
                    LOGS.MailManager,
                    "Retrying once after 2s delay ...",
                );

                await new Promise((resolve) => setTimeout(resolve, 2000));
                viewFilterSearchResults =
                    await this.dataManager.searchInternetMessageIdsFromViewFilter(
                        viewFilter,
                        limit,
                    );
            }
        }

        // ═══════════════════════════════════════════════════════════════════════════
        // CAS 2: RECHERCHE UNIQUEMENT IA
        // ═══════════════════════════════════════════════════════════════════════════
        else if (hasAIProperties && !hasNativeProperties) {
            logger.info(LOGS.MailManager, "Recherche uniquement IA");

            // TODO - Implémenter les filtres de keyword en DB sans casser l'intersect avec Graph
            logger.warn(
                LOGS.MailManager,
                `     => Envoie d'un tableau vide [] pour les keywords lors de la recherche en DB`,
            );
            // La recherche rend la date, clé d'ordre de la vue.
            viewFilterSearchResults =
                await this.apiManager.searchMailsByCategoriesAndStatus(
                    // [WS-C / AB#1576] migré Core → Api-Backend
                    viewFilter.categories || [],
                    viewFilter.mailStatus || [],
                    // TODO - Implement Keywords search in DB
                    /* viewFilter.keywords   || */ [],
                    viewFilter.sortOrder || CustomViewSortOrder.DESC,
                    limit,
                );
        }

        // ═══════════════════════════════════════════════════════════════════════════
        // CAS 3: RECHERCHE MIXTE (IA + NATIVE) - INTERSECTION DES RESULTATS
        // ═══════════════════════════════════════════════════════════════════════════
        else if (hasAIProperties && hasNativeProperties) {
            logger.info(LOGS.MailManager, "Recherche mixte (IA + native)");

            // Recherche parallèle sur les deux systèmes
            // TODO - Implémenter les filtres de keyword en DB sans casser l'intersect avec Graph
            logger.warn(
                LOGS.MailManager,
                `     => Envoie d'un tableau vide [] pour les keywords lors de la recherche en DB`,
            );

            // Fonction helper pour retry
            const searchNativeWithRetry = async (dataManager: DataManager) => {
                try {
                    return await dataManager.searchInternetMessageIdsFromViewFilter(
                        viewFilter,
                        undefined,
                    );
                } catch (error) {
                    // 429 déjà géré exhaustivement par le backoff de callGraphEndPoint → ne pas rejouer
                    if (isThrottleError(error)) throw error;
                    logger.error(
                        [LOGS.MailManager],
                        `Error during searchInternetMessageIdsFromViewFilter : ${error}`,
                    );
                    logger.warn(
                        LOGS.MailManager,
                        "Retrying once after 2s delay ...",
                    );
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    return await dataManager.searchInternetMessageIdsFromViewFilter(
                        viewFilter,
                        undefined,
                    );
                }
            };

            const [nativeResults, aiEntries] = await Promise.all([
                searchNativeWithRetry(this.dataManager),
                this.apiManager.searchMailsByCategoriesAndStatus(
                    // [WS-C / AB#1576] migré Core → Api-Backend
                    viewFilter.categories || [],
                    viewFilter.mailStatus || [],
                    /* viewFilter.keywords   || */ [],
                    viewFilter.sortOrder || CustomViewSortOrder.DESC,
                    undefined, // Pas de limit pour avoir tous les résultats
                ),
            ]);

            // Intersection : on conserve les résultats NATIFS. Les deux sources portent la date,
            // mais n'en garder qu'UNE evite qu'un meme mail soit date differemment d'une session
            // a l'autre selon le chemin emprunte.
            const aiIdSet = new Set(
                aiEntries.map((entry) => entry.internetMessageId),
            );
            viewFilterSearchResults = nativeResults.filter((result) =>
                aiIdSet.has(result.internetMessageId),
            );

            // Apply limit on IDs to avoid fetching unnecessary mails
            if (limit) {
                viewFilterSearchResults = viewFilterSearchResults.slice(
                    0,
                    limit,
                );
            }

            logger.info(
                LOGS.MailManager,
                `Recherche mixte: ${nativeResults.length} résultats natifs, ${aiEntries.length} résultats IA, ${viewFilterSearchResults.length} en intersection`,
            );
        }

        // ═══════════════════════════════════════════════════════════════════════════
        // CAS 4: AUCUNE PROPRIETE DE RECHERCHE
        // ═══════════════════════════════════════════════════════════════════════════
        else {
            logger.warn(
                LOGS.MailManager,
                "Aucune propriété de recherche valide trouvée",
            );
            return [];
        }

        return viewFilterSearchResults;
    }

    /** Écrit/maj une copie sous (internetMessageId, parentFolderId). */
    public _setMailInRecord(mail: Mail): void {
        // Q-F1 : stampe l'identité de copie au point d'écriture (chokepoint Record) → fiablement présente
        // sur tout mail rendu au FrontEnd. (uniqueMailvistaId = fonction du jumeau ; `mail` ne la shadow pas.)
        mail.uniqueMailvistaId = uniqueMailvistaId(mail);

        if (mail.aiDetails?.toDo) {
            mail.aiDetails.toDo = mail.aiDetails.toDo.map((todo) => ({
                ...todo,
                internetMessageId: mail.internetMessageId,
                checkedDate: todo.checkedDate ?? null,
            }));
        }

        let slots = this.mailsRecordByInternetMessageId[mail.internetMessageId];
        if (!slots) {
            slots = new Map<string, Mail>();
            this.mailsRecordByInternetMessageId[mail.internetMessageId] = slots;
        }

        // C (pull) : une copie entrante vide hérite des PJ d'une copie sœur déjà enrichie (couvre le
        // timing self-sent). Vraies PJ seulement ; `slots` n'a pas encore la copie entrante. #1332
        if ((mail.attachments?.length ?? 0) === 0) {
            for (const sibling of slots.values()) {
                if (sibling.parentFolderId === mail.parentFolderId) continue; // ignorer l'ancien occupant du même slot
                if ((sibling.attachments?.length ?? 0) > 0) {
                    mail.attachments = [...sibling.attachments];
                    mail.hasAttachment = true;
                    logger.info(
                        [LOGS.MailManager],
                        `C(pull): copie entrante ${mail.internetMessageId}@${mail.parentFolderId} a hérité des PJ d'une copie sœur`,
                    );
                    break;
                }
            }
        }

        const previousOccupant = slots.get(mail.parentFolderId);
        if (
            previousOccupant &&
            this._attachmentsFetchedSet.has(uniqueMailvistaId(previousOccupant))
        ) {
            mail.message = previousOccupant.message;
            mail.uniqueBody = previousOccupant.uniqueBody;
            mail.attachments = previousOccupant.attachments;
            mail.hasAttachment = previousOccupant.hasAttachment;
        }

        // Taille de tuile : calculée ici plutôt que reprise du DTO Backend, qui ne voit pas le retrait
        // des PJ inline fait à l'enrichissement.
        mail.componentSize = this._determineMailComponentSize(mail);

        slots.set(mail.parentFolderId, mail);
    }

    /**
     * Reset du mail record après son déplacement dans la corbeille
     *  - parentFolderId set to 'deleteditems'
     *  - aidetails set to undefined
     *  - is Focus set to false
     *  - isUserProcessed set to false
     *  - smartLabel set to ""
     *
     * @param mailId - Id du mail
     * @param internetMessageId - internetMessageId du mail
     */
    public _updateRecordWithoutAnalysis(
        mailId: string,
        internetMessageId: string,
        sourceFolderId?: string,
    ) {
        try {
            if (!internetMessageId) {
                logger.warn(
                    [LOGS.MailManager],
                    "Cannot update Mail Record : internetMessageId is empty",
                );
                return;
            }

            // WS5c-3 : move-to-trash = re-key du slot Map (la copie change de dossier). On capture le
            // dossier d'origine AVANT mutation, puis on déplace le slot `oldFolderId → deletedItems`
            // (corrige la dette WS3 : avant, parentFolderId était muté en place sans re-keyer le slot).
            // WS5d : `sourceFolderId` fourni ⇒ on opère sur la COPIE exacte (`_getCopy`) ; sinon fallback
            //   sur la copie canonique (appelants legacy : affichage Corbeille, bloc deletedItems de fetch).
            const mail =
                sourceFolderId !== undefined
                    ? this._getCopy(internetMessageId, sourceFolderId)
                    : this._getCanonicalMail(internetMessageId);
            if (mail) {
                const oldFolderId = mail.parentFolderId;
                const newFolderId =
                    this.deleteditemsFolderId || MailFolder.Deleteditems;

                mail.id = mailId;
                mail.parentFolderId = newFolderId;
                // Q-F1 : parentFolderId muté (move) → re-stamper l'identité de copie pour rester cohérent.
                mail.uniqueMailvistaId = uniqueMailvistaId(mail);
                mail.aiDetails = undefined;
                mail.isFocus = false;
                mail.isUserProcessed = false;
                mail.isUserEdited = false;
                mail.smartLabel = undefined;
                mail.componentSize = this._determineMailComponentSize(mail);

                // Re-key du slot. Garde anti-clobber : si une copie deletedItems existe déjà (ex. bloc
                // deletedItems de fetchMissing), on retire seulement l'ancien slot sans écraser l'existante.
                const slots =
                    this.mailsRecordByInternetMessageId[internetMessageId];
                if (slots && oldFolderId !== newFolderId) {
                    slots.delete(oldFolderId);
                    // Purge (pas de re-key) de la clé fetched de la copie retirée du slot `oldFolderId` :
                    // évite la clé orpheline (false-positive du guard si une copie ré-occupe `oldFolderId`
                    // plus tard → enrichissement CID sauté, #1332). On NE marque PAS `newFolderId` comme
                    // fetched : ce point est aussi traversé par le reperage (`:4501`, sans sourceFolderId) où
                    // marquer la destination peut laisser une copie « fetched mais body brut » → image cassée.
                    this._forgetAttachmentsFetched({
                        id: "",
                        internetMessageId,
                        parentFolderId: oldFolderId,
                    });
                    if (!slots.has(newFolderId)) {
                        slots.set(newFolderId, mail);
                    }
                }
            } else {
                logger.info(
                    [LOGS.MailManager],
                    `InternetMessageId ${internetMessageId} was not found in Mail Record`,
                );
            }
        } catch (error) {
            logger.error(
                [LOGS.MailManager],
                `Error updating Mail Record : ${error}`,
            );
        }
    }

    public async fetchMissingMailViewContentIfAny(
        internetMessageIds: string[],
    ): Promise<void> {
        // 1. Diff vs Record (dédup de l'entrée au passage) → ids absents à fetcher
        const missing: string[] = [];
        const seenMissing = new Set<string>();
        for (const imid of internetMessageIds) {
            if (seenMissing.has(imid)) continue;
            seenMissing.add(imid);
            if (!(imid in this.mailViewContentRecordByInternetMessageId))
                missing.push(imid);
        }
        if (missing.length === 0) {
            logger.info(
                LOGS.AB1727,
                `[AB#1727-SDK] fetchMissingMailViewContent: requested=${internetMessageIds.length} missing=0 (all cached, no /mails/content call)`,
            );
            return;
        }

        // 2. Partition : ids déjà en vol (partager la promesse) vs à fetcher
        const inFlight: Promise<void>[] = [];
        const toFetch: string[] = [];
        for (const imid of missing) {
            const existing = this._inFlightViewContentFetches.get(imid);
            if (existing) inFlight.push(existing);
            else toFetch.push(imid);
        }
        logger.info(
            LOGS.AB1727,
            `[AB#1727-SDK] fetchMissingMailViewContent: requested=${internetMessageIds.length} missing=${missing.length} toFetch=${toFetch.length} alreadyInFlight=${missing.length - toFetch.length}`,
        );

        // 3. Chunker les ids à fetcher (borne le payload + isole les échecs par lot), puis exécuter les
        //    lots avec une CONCURRENCE BORNÉE (pool). Sans borne, une grosse view (des centaines de mails)
        //    déclencherait une rafale de POST /mails/content simultanés → autant de fan-out Graph côté
        //    backend → risque 429 (cf. #1632/#1666 ; le reste du code est prudent : batch-de-19 séquentiel,
        //    THREAD_CONCURRENCY). La dédup inter-appelants reste garantie : chaque id est enregistré
        //    SYNCHRONEMENT sur la « gate » de son lot, même si l'exécution du lot est différée par le pool.
        const BATCH_SIZE = 50; // mails par requête POST /mails/content
        const MAX_CONCURRENT_CHUNKS = 4; // lots en vol simultanément (borne le fan-out backend)

        const chunks: string[][] = [];
        for (
            let startIdx = 0;
            startIdx < toFetch.length;
            startIdx += BATCH_SIZE
        ) {
            chunks.push(toFetch.slice(startIdx, startIdx + BATCH_SIZE));
        }

        // Une « gate » par lot : promesse enregistrée en in-flight dès maintenant (dédup), résolue quand
        // le pool a traité le lot (best-effort → toujours résolue, jamais rejetée).
        const gates = chunks.map(() => {
            let resolve!: () => void;
            const promise = new Promise<void>((r) => {
                resolve = r;
            });
            return { promise, resolve };
        });
        chunks.forEach((chunk, ci) => {
            const gatePromise = gates[ci].promise;
            for (const imid of chunk)
                this._inFlightViewContentFetches.set(imid, gatePromise);
            gatePromise.finally(() => {
                for (const imid of chunk)
                    this._inFlightViewContentFetches.delete(imid);
            });
            inFlight.push(gatePromise);
        });

        // Pool borné : au plus MAX_CONCURRENT_CHUNKS lots traités en parallèle.
        let chunkIdx = 0;
        const chunkWorker = async (): Promise<void> => {
            while (chunkIdx < chunks.length) {
                const ci = chunkIdx++;
                try {
                    await this._fetchMailViewContentChunk(chunks[ci]); // best-effort : ne throw pas
                } finally {
                    gates[ci].resolve();
                }
            }
        };
        // Fire-and-forget : les workers avalent déjà toute erreur (chunk best-effort + finally resolve la gate),
        // mais on durcit avec un `.catch` no-op pour garantir zéro rejet non géré (M4, review PR #210).
        void Promise.all(
            Array.from(
                { length: Math.min(MAX_CONCURRENT_CHUNKS, chunks.length) },
                () => chunkWorker(),
            ),
        ).catch(() => {
            /* best-effort : les gates restent résolues par le finally du worker */
        });

        await Promise.allSettled(inFlight);
    }

    /**
     * Ajoute au Record les mails absents. Les appels concurrents sur les mêmes ids partagent un seul
     * batch API ; les ids en échec sont retentés une fois.
     * @param _remainingRetries interne : ne jamais le fixer, passer `undefined` pour atteindre le paramètre suivant.
     * @param analysisPromisesCollector tableau vide à fournir pour suivre l'analyse IA : chaque batch y
     *   pousse sa Promise, `Promise.allSettled(collector)` attend la fin. Absent : fire-and-forget.
     */
    public async fetchMissingMailsIfAnyByInternetMessageIds(
        internetMessageIds: string[],
        askedBy: string = "unknown",
        forceMailAnalysis: boolean = false,
        batchDelayMs: number = 2000,
        _remainingRetries: number = 1,
        analysisPromisesCollector?: Promise<void>[],
    ): Promise<void> {
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `fetchMissingMailsIfAnyByInternetMessageIds for ${askedBy}`,
        );
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `internetMessageIds to fetch if missing`,
            internetMessageIds,
        );

        // WS4 — chemin CANONIQUE/SmartLabel : chaque message cible sa copie INBOX (slot canonique).
        //   « manquant » = slot inbox absent du Record. Le fetch passe par l'API enrichie.
        //   ⚠️ Correction d'affichage réelle (copie inbox) conditionnée à WS1 Lot C (API renvoyant
        //      pickCanonicalCopy) : sans lui, l'API peut renvoyer une copie non-inbox et le slot inbox
        //      resterait vide (refetch répété). On code en avance de phase, vérif runtime post-Backend.
        if (!this.inboxFolderId) {
            logger.error(
                [LOGS.MailManager],
                `fetchMissingMailsIfAnyByInternetMessageIds (${askedBy}) — inboxFolderId pas encore prêt, abandon (déc. #7 : pas de fetch canonique sans contexte)`,
            );
            return;
        }
        const inboxFolderId = this.inboxFolderId;
        const targets: MailCopyFetchTarget[] = [
            ...new Set(internetMessageIds),
        ].map((internetMessageId) => ({
            internetMessageId,
            folderId: inboxFolderId,
        }));

        await this._fetchMissingCopies(
            targets,
            {
                path: MailCopyFetchPath.Canonical,
                isMissing: this._isSlotMissing,
                fetch: (toFetch) =>
                    this._fetchAndProcessMails(
                        toFetch.map((t) => t.internetMessageId),
                        forceMailAnalysis,
                        batchDelayMs,
                        analysisPromisesCollector,
                    ),
            },
            askedBy,
            _remainingRetries,
        );
    }

    public getMailViewContentFromRecord(
        internetMessageIds: string[],
    ): readonly MailViewContent[] {
        const result: MailViewContent[] = [];
        const seen = new Set<string>();
        for (const imid of internetMessageIds) {
            if (seen.has(imid)) continue;
            seen.add(imid);
            const content = this.mailViewContentRecordByInternetMessageId[imid];
            if (content) result.push(content);
        }
        return result;
    }

    /**
     * Composition datée d'une vue : chaque entrée porte sa date d'envoi, clé d'ordre que l'API
     * stocke. Une entrée sans date reste membre de la vue et ira en fin de liste.
     */
    public async getViewFilterMailEntries(
        viewFilter: ViewFilter,
        limit: number | undefined = undefined,
    ): Promise<CustomViewMailEntry[]> {
        const results = await this._searchViewFilterResults(viewFilter, limit);

        return results.map((result) => ({
            internetMessageId: result.internetMessageId,
            sentDateTime: toValidDate(result.sentDateTime),
        }));
    }

    /**
     * Trie un tableau d'emails par _ranking décroissant, ou par sentDateTime décroissant à défaut
     * @param mails - Tableau d'emails à trier
     * @returns Nouveau tableau trié (ne modifie pas le tableau original)
     */
    public _sortMailsByRanking(mails: Mail[]): Mail[] {
        if (!mails || mails.length === 0) {
            return [];
        }

        return [...mails].sort((a, b) => {
            const hasRankingA =
                a._ranking != null && a._ranking.totalScore != null;
            const hasRankingB =
                b._ranking != null && b._ranking.totalScore != null;

            // 1. Séparer: mails avec ranking d'abord, puis sans ranking
            if (hasRankingA && !hasRankingB) return -1;
            if (!hasRankingA && hasRankingB) return 1;

            // 2. Si les deux ont un ranking: trier par totalScore décroissant
            if (hasRankingA && hasRankingB) {
                return b._ranking!.totalScore! - a._ranking!.totalScore!;
            }

            // 3. Si aucun n'a de ranking: trier par sentDateTime décroissant
            const dateA = a.sentDateTime;
            const dateB = b.sentDateTime;

            // Gestion des dates null/undefined
            if (dateA == null && dateB == null) return 0;
            if (dateA == null) return 1;
            if (dateB == null) return -1;

            // Conversion en timestamps
            const timestampA =
                typeof dateA === "string" ? new Date(dateA).getTime() : dateA;
            const timestampB =
                typeof dateB === "string" ? new Date(dateB).getTime() : dateB;

            // Vérification des dates invalides
            if (isNaN(timestampA) && isNaN(timestampB)) return 0;
            if (isNaN(timestampA)) return 1;
            if (isNaN(timestampB)) return -1;

            return timestampB - timestampA;
        });
    }

    public _sortMailsBySentDateTime(
        mails: Mail[],
        order: CustomViewSortOrder,
    ): Mail[] {
        if (!mails || mails.length === 0) {
            return [];
        }

        const byInternetMessageId = (a: Mail, b: Mail) =>
            this._compareByInternetMessageId(a, b);

        return [...mails].sort((a, b) => {
            const dateA = a.sentDateTime;
            const dateB = b.sentDateTime;

            // Gestion des valeurs null/undefined - toujours en fin de liste
            if (dateA === null || dateA === undefined) {
                if (dateB === null || dateB === undefined) {
                    return byInternetMessageId(a, b);
                }
                return 1; // A est null, B n'est pas null -> A après B
            }

            if (dateB === null || dateB === undefined) {
                return -1; // B est null, A n'est pas null -> B après A
            }

            // Conversion en timestamps pour comparaison
            const timestampA =
                typeof dateA === "string" ? new Date(dateA).getTime() : dateA;
            const timestampB =
                typeof dateB === "string" ? new Date(dateB).getTime() : dateB;

            // Vérification que les dates sont valides
            if (isNaN(timestampA) || isNaN(timestampB)) {
                // Si une date est invalide, on la traite comme null
                if (isNaN(timestampA) && isNaN(timestampB)) {
                    return byInternetMessageId(a, b);
                }
                return isNaN(timestampA) ? 1 : -1;
            }

            if (timestampA === timestampB) {
                return byInternetMessageId(a, b);
            }

            // Tri selon l'ordre demandé
            if (order === CustomViewSortOrder.ASC) {
                return timestampA - timestampB;
            } else {
                return timestampB - timestampA;
            }
        });
    }

    /**
     * Trie un tableau d'emails par sentDateTime
     * @param mails - Tableau d'emails à trier
     * @param order - Ordre de tri
     * @returns Nouveau tableau trié (ne modifie pas le tableau original)
     */
    /**
     * Départage à date égale : `internetMessageId` croissant, MÊME clé que l'API. Sans clé
     * explicite, le tri stable rend l'ordre d'entrée, qui peut différer de celui de l'API et
     * provoquer une réécriture inutile de toute la vue.
     */
    private _compareByInternetMessageId(a: Mail, b: Mail): number {
        return (a.internetMessageId ?? "").localeCompare(
            b.internetMessageId ?? "",
        );
    }

    /**
     * @method pollForNewMails
     * Vérifie périodiquement la présence de nouveaux emails
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @private
     */
    private async pollForNewMails() {
        // Horloge partagée : BannerManager y raccroche sa revérification, sans timer de plus.
        this.eventBus.emit("mails-polling-tick");

        if (this.mailProcessingInProgress) {
            logger.info(
                [LOGS.AnalysisLifecycle],
                `Polling already in progress, skipping this cycle`,
            );
            return;
        }

        this.mailProcessingInProgress = true;
        logger.info([LOGS.AnalysisLifecycle], "Polling for new mails");

        try {
            // Finds latest mail in record

            // Watermark sur les copies Inbox uniquement (voir selectLatestInboxMail) : sinon la copie
            // Sent du mail self-sent élève latestDate et masque la copie Inbox au polling.
            const latestMail = selectLatestInboxMail(
                this._allCopies(),
                this.mailRecord.inboxFolderId,
                this.mailRecord.deleteditemsFolderId,
            );

            if (!latestMail) {
                logger.warn(
                    [LOGS.AnalysisLifecycle],
                    `  !!! No mails found in record yet, polling skipped.`,
                );
                return;
            }

            const latestDate = new Date(
                new Date(latestMail.sentDateTime ?? "").getTime() + 1,
            ); // Dirty code to avoir Eslint error on undefined date, cannot happen because filtrered
            logger.info(
                LOGS.AnalysisLifecycle,
                ` latest mail in Record : ${new Date(latestDate).toString()}`,
            );

            // Gets new mails ids since latest from Graph
            // Gets new mail from DataManager
            let newMailsIdTuples: MailIds[] = [];
            try {
                newMailsIdTuples = await this.apiManager.GetMailIdsSince(
                    latestDate.toISOString(),
                );
            } catch (error) {
                logger.error(
                    [LOGS.MailManager],
                    `Error in pollForNewMails: ${error}`,
                );
                logger.warn(
                    LOGS.MailManager,
                    "Returning [] as result for newMailsIdTuples",
                );
            }

            // If no new mails, returns
            if (!newMailsIdTuples || newMailsIdTuples.length === 0) {
                logger.info(
                    LOGS.AnalysisLifecycle,
                    `Polling received no new mails`,
                );
                return;
            }

            // Double check not to add already existing emails
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `Polling received ${newMailsIdTuples?.length!} new mails`,
            );

            // WS7 : filtre slot-aware (inbox) — remplace l'ancien duo invalidateStaleNonInboxCopies + filtre
            // message-level. Un message déjà présent en copie Sent (self-sent seedé par mail-sent_handler) mais
            // SANS copie inbox doit PASSER le filtre → fetchMissingMailsIfAnyByInternetMessageIds récupère le
            // slot inbox manquant. On ne dédup que sur la copie inbox déjà présente (vrai doublon). (dette WS3-(c) levée.)
            newMailsIdTuples = newMailsIdTuples.filter(
                (newMail) =>
                    !(
                        this.mailRecord.inboxFolderId &&
                        this.mailRecord.byInternetMessageId[
                            newMail.internetMessageId
                        ]?.get(this.mailRecord.inboxFolderId)
                    ),
            );
            if (newMailsIdTuples.length === 0) {
                logger.warn(
                    LOGS.AnalysisLifecycle,
                    `New mail received already exist in mail list, this should not happen`,
                );
                return;
            }

            // Fetch missing mails from Graph and add them to record
            const newMailsInternetMessageIds = newMailsIdTuples.map(
                (newMailsIdTuple) => newMailsIdTuple.internetMessageId,
            );
            await this.fetchMissingMailsIfAnyByInternetMessageIds(
                newMailsInternetMessageIds,
                `MailSync.pollForNewMails`,
            );
            const newGraphOnlyMails = newMailsInternetMessageIds
                .map((internetMessageId) =>
                    this._getCanonicalMail(internetMessageId),
                )
                .filter((mail): mail is Mail => mail !== undefined);

            // NAV UPDATE
            if (this.mailRecord.inboxFolderId) {
                // this._updateGeneralNavBarItemCount(this.mailRecord.inboxFolderId, newGraphOnlyMails.length)
                this.eventBus.emit("navigation-asking-for-update");
            } else {
                logger.error(
                    LOGS.MailManager,
                    `pollForNewMails cannot _updateGeneralNavBarItemCount because inboxFolderId is not set yet`,
                );
            }

            // Emit new mails event
            logger.info(
                [LOGS.AnalysisLifecycle],
                "Polling emit <mails-new-mails> and <thread-updated>",
            );
            this.eventBus.emit("mails-new-mails", newGraphOnlyMails);
        } catch (error) {
            logger.error(
                [LOGS.AnalysisLifecycle],
                `Error polling for new mails: ${error}`,
            );
        } finally {
            this.mailProcessingInProgress = false;
        }
    }

    /**
     * Chemin vue dossier (Sent, Trash…) : récupère la copie du dossier listé par projection Graph
     * directe, sans analyse ni DB. « Manquant » = slot du dossier absent du Record.
     */
    public async fetchMissingFolderCopiesIfAny(
        copyRefs: MailCopyRef[],
        askedBy: string = "unknown",
    ): Promise<void> {
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `fetchMissingFolderCopiesIfAny for ${askedBy} (${copyRefs.length} refs)`,
        );

        // Le listing du dossier porte l'`id` Graph COURANT de chaque copie. Avant de
        // ne fetcher que les slots absents, on en profite pour réconcilier l'`id` des slots DÉJÀ présents
        // mais dont le handle Graph aurait expiré (sinon `_findMissingCopies` les ignore et l'id périmé
        // persiste → 404 sur attachments/getMailById/etc.). Réconciliation en mémoire, sans fetch.
        this._reconcileCopyGraphIds(copyRefs);

        const targets: MailCopyFetchTarget[] = copyRefs.map((ref) => ({
            internetMessageId: ref.internetMessageId,
            folderId: ref.parentFolderId,
            graphId: ref.id,
        }));

        await this._fetchMissingCopies(
            targets,
            {
                path: MailCopyFetchPath.Folder,
                isMissing: this._isSlotMissing,
                fetch: (toFetch) => this._fetchAndProcessFolderCopies(toFetch),
            },
            askedBy,
        );
    }

    /**
     * Route backend sans restriction de dossier, à réserver aux vues non inbox-derived : une vue
     * SmartLabel passe par `fetchMissingMailsIfAnyByInternetMessageIds`.
     */
    public async fetchMissingMailsInAnyFolderIfAny(
        internetMessageIds: string[],
        askedBy: string = "unknown",
    ): Promise<void> {
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `fetchMissingMailsInAnyFolderIfAny for ${askedBy}`,
        );

        if (!this.mailRecord.inboxFolderId) {
            logger.error(
                [LOGS.MailManager],
                `fetchMissingMailsInAnyFolderIfAny (${askedBy}) — inboxFolderId pas encore prêt, abandon`,
            );
            return;
        }

        // folderId = inbox : clé de dédup stable par message, pas une cible de fetch.
        const inboxFolderId = this.mailRecord.inboxFolderId;
        const targets: MailCopyFetchTarget[] = [
            ...new Set(internetMessageIds),
        ].map((internetMessageId) => ({
            internetMessageId,
            folderId: inboxFolderId,
        }));

        await this._fetchMissingCopies(
            targets,
            {
                path: MailCopyFetchPath.AnyFolder,
                isMissing: this._isMessageUnknown,
                fetch: (toFetch) =>
                    this._fetchAndProcessMailsInAnyFolder(
                        toFetch.map((t) => t.internetMessageId),
                    ),
            },
            askedBy,
        );
    }

    /**
     * Chemin AnyFolder : manquant = aucune occurrence connue. Ne PAS réutiliser `_isSlotMissing` —
     * un mail envoyé n'a pas de slot inbox et n'en aura jamais : re-fetch infini à chaque pagination.
     */
    private _isMessageUnknown = (target: MailCopyFetchTarget): boolean => {
        const copies =
            this.mailRecord.byInternetMessageId[target.internetMessageId];
        return copies === undefined || copies.size === 0;
    };

    /**
     * WS4 — Projection directe Graph d'une copie de dossier (Sent/Trash/…). Pas d'analyse IA, pas de DB
     * (invariant : dossiers non-inbox = pures projections Graph). Range chaque copie sous son slot.
     */
    private async _fetchAndProcessFolderCopies(
        targets: MailCopyFetchTarget[],
    ): Promise<void> {
        // Cibles sans graphId : impossible de projeter la copie (slot ignoré, jamais récupérable → pas de retry).
        const fetchable: MailCopyFetchTarget[] = [];
        for (const t of targets) {
            if (t.graphId) {
                fetchable.push(t);
            } else {
                logger.warn(
                    [LOGS.MailManager],
                    `_fetchAndProcessFolderCopies - pas de graphId pour ${t.internetMessageId}@${t.folderId}, slot ignoré`,
                );
            }
        }
        if (fetchable.length === 0) return;

        // WS6 (optim) — fetch GROUPÉ via Graph $batch (≤20/req) au lieu de N getMailById séquentiels.
        // Isolation par copie préservée : une copie en échec (ex. 404 graphId périmé) revient dans
        // failedIds sans faire échouer les autres ; les copies réussies sont déjà dans le Record. On
        // relance via le throw final pour que la couche retry (_fetchMissingCopies) ne re-tente que les
        // slots encore absents (filtrés par _findMissingCopies).
        const { mails, failedIds } = await this.dataManager.getMailsByIds(
            fetchable.map((t) => t.graphId!),
        );

        for (const mail of mails) {
            this._setMailInRecord(mail);
        }

        if (failedIds.length > 0) {
            logger.warn(
                [LOGS.MailManager],
                `_fetchAndProcessFolderCopies - ${failedIds.length}/${fetchable.length} copie(s) en échec (slots isolés)`,
            );
            throw new Error(
                `_fetchAndProcessFolderCopies : ${failedIds.length}/${fetchable.length} copie(s) en échec (copies réussies conservées dans le Record)`,
            );
        }
    }

    /**
     * Jumeau de `_fetchAndProcessMails` pour les vues non inbox-derived. Deux différences :
     * aucune réduction canonique (l'affichage filtre), et seules les occurrences inbox partent en
     * analyse — soumettre une occurrence Sent à l'IA/DB serait la régression P-D de WS7b.
     */
    private async _fetchAndProcessMailsInAnyFolder(
        internetMessageIds: string[],
    ): Promise<void> {
        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `  Fetching ${internetMessageIds.length} missing mail copies from ApiManager ... `,
        );

        if (!this.mailRecord.inboxFolderId) {
            throw new Error(
                "inboxFolderId not available yet — copies fetch aborted (startup ordering violated)",
            );
        }

        let response: GetMailsInAnyFolderResponseBody;
        try {
            response =
                await this.apiManager.getMailsInAnyFolderByInternetMessageIds(
                    internetMessageIds,
                    this.mailRecord.inboxFolderId,
                );
        } catch (error) {
            logger.error(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                `  !!! Error fetching mails in any folder from ApiManager: ${error}`,
            );
            // Sur échec, « supprimé » et « réseau en panne » sont indiscernables → ne rien toucher.
            throw new Error(
                `Failed to fetch mails in any folder: ${error.message}`,
            );
        }

        logger.info(
            [LOGS.AnalysisLifecycle, LOGS.MailManager],
            `_fetchAndProcessMailsInAnyFolder - copies retournées par le backend`,
            [...response.qualifiedMails, ...response.mails].map((m) => ({
                internetMessageId: m.internetMessageId,
                graphId: m.id,
                parentFolderId: m.parentFolderId,
            })),
        );

        // Suppressions CONFIRMÉES uniquement (témoin AB#1719) ; absent des trois listes = statu quo.
        response.deletedInternetMessageIds.forEach((internetMessageId) =>
            this._evictInboxCopyOnBackendVerdict(internetMessageId),
        );

        // Occurrences hors inbox : rangées sous leur slot, sans analyse ni DB.
        const analysableMails: (BaseMail | QualifiedMail)[] = [
            ...response.qualifiedMails,
        ];
        for (const mail of response.mails) {
            if (mail.parentFolderId === this.mailRecord.inboxFolderId) {
                analysableMails.push(mail);
            } else {
                this._setMailInRecord(mail as Mail);
            }
        }

        this._addMailsToRecordAndLaunchAnalysisForNew(analysableMails);
    }

    /**
     * @method findMissingInternetMessageIds
     * Identifie les internetMessageIds qui ne sont pas présents dans le record mailsByInternetMessageId
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string[]} internetMessageIds - Liste des internetMessageIds à vérifier
     * @output {string[]} Liste des internetMessageIds manquants
     */
    public _findMissingInternetMessageIds(
        internetMessageIds: string[],
    ): string[] {
        if (!internetMessageIds || internetMessageIds.length === 0) {
            return [];
        }

        // Déduplication : un même internetMessageId peut arriver en double (ex. un thread
        // retournant 2× le même mail). Sans Set, l'id manquant serait fetché/traité deux fois.
        const missingIds: string[] = [...new Set(internetMessageIds)].filter(
            (id) => !this.mailRecord.byInternetMessageId[id],
        );
        if (missingIds.length > 0) {
            logger.info(
                [LOGS.MailManager],
                `Found ${missingIds.length} missing internetMessageIds in Record`,
            );
        }

        return missingIds;
    }

    /**
     * Réconciliation de l'`id` Graph d'une copie DÉJÀ présente dans le Record.
     *
     * Le `id` Graph est un handle de MUTATION : il change au déplacement / à la ré-indexation d'un
     * message. Une copie peut donc rester dans le Record avec un `id` PÉRIMÉ alors qu'elle existe
     * toujours dans le même dossier → 404 persistants sur toutes les opérations folder-scoped qui
     * s'appuient sur cet id (getMailAttachments, getMailById, setReadStatus, deleteMail, moveMail,
     * source de reply/forward).
     *
     * `_findMissingCopies` ne re-fetch QUE les slots ABSENTS → il ne corrige jamais l'id d'un slot
     * présent. Or le listing du dossier (`fetchGeneralInternetMessageIds`) fournit dans chaque
     * `MailCopyRef` l'`id` COURANT de la copie : on l'adopte comme source de vérité pour le slot existant.
     *
     * Décisions :
     *  - D-1 : appelé uniquement depuis le chemin « vue Dossier » (seul à porter un MailCopyRef.id frais) ;
     *  - D-2 : AUCUN `mail-updated` émis → la clé front est `uniqueMailvistaId` (= imid::folderId,
     *          inchangé par l'id) ; la copie réconciliée est vue au prochain fetch frais (cf. C-4) ;
     *  - D-3 : on n'écrit QUE si l'`id` diffère réellement (idempotent) ;
     *  - D-4 : périmètre « MÊME slot, id différent » UNIQUEMENT — pas de gestion des déplacements (un
     *          move crée un nouveau slot fetché normalement ; le slot orphelin relève de la suppression).
     *
     * Réconciliation EN MÉMOIRE (Record) ; `uniqueMailvistaId` (imid::folderId) n'est pas affecté par l'id.
     */
    private _reconcileCopyGraphIds(copyRefs: MailCopyRef[]): void {
        if (!copyRefs || copyRefs.length === 0) return;

        for (const ref of copyRefs) {
            // On ne vise que le SLOT EXACT du dossier listé (imid + parentFolderId du ref).
            const existing = this.mailRecord.byInternetMessageId[
                ref.internetMessageId
            ]?.get(ref.parentFolderId);
            // Réconcilier seulement un slot DÉJÀ présent dont l'id Graph diffère de l'id courant du dossier.
            if (existing && ref.id && existing.id !== ref.id) {
                logger.info(
                    [LOGS.MailManager],
                    `D: réconciliation id Graph périmé pour ${ref.internetMessageId}@${ref.parentFolderId} (${existing.id} → ${ref.id})`,
                );
                existing.id = ref.id;
            }
        }
    }

    public _beginMailProcessing() {
        this.mailProcessingInProgress = true;
    }

    public _endMailProcessing() {
        this.mailProcessingInProgress = false;
    }

    public _startMailPolling() {
        if (!this.pollingInterval) {
            this.pollingInterval = setInterval(
                () => this.pollForNewMails(),
                this.pollingDelayInMs,
            );
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                "Mail polling started",
            );
        }
    }

    public _stopMailPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
            logger.info(
                [LOGS.AnalysisLifecycle, LOGS.MailManager],
                "Mail polling stopped",
            );
        }
    }
}
